import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Card, CardContent, Skeleton, Alert, IconButton,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import HeadsetMicRoundedIcon from "@mui/icons-material/HeadsetMicRounded";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell,
} from "recharts";
import { getData, API_ENDPOINTS } from "../../services/apiService.js";

// ── Brand palette ─────────────────────────────────────────────────────────
const T = {
  teal:    "#007C84", tealDk: "#016268", tealLt: "#E0F4F5",
  orange:  "#FF7900", orangeLt: "#FFF0E0",
  emerald: "#059669", emeraldLt: "#D1FAE5",
  rose:    "#E11D48", roseLt:   "#FFE4E6",
  amber:   "#D97706", amberLt:  "#FEF3C7",
  indigo:  "#4F46E5", indigoLt: "#EEF2FF",
  violet:  "#7C3AED", violetLt: "#EDE9FE",
  bg:      "#F7FAFC", border: "#E2E8F0",
  text:    "#0F172A", muted:  "#64748B",
};

// Status to brand colour
const STATUS_COLORS = {
  submitted:         T.amber,
  under_review:      T.indigo,
  awaiting_response: T.violet,
  resolved:          T.emerald,
  closed:            T.muted,
  reopened:          T.rose,
  escalated:         T.rose,
};

const PIE_PALETTE = [T.teal, T.orange, T.indigo, T.emerald, T.amber, T.rose, T.violet, "#0891B2"];

const catLabel = (s) => s?.replace(/_/g, " ")?.replace(/\b\w/g, c => c.toUpperCase()) || s;

// ── KPI tile ──────────────────────────────────────────────────────────────
const KpiTile = ({ label, value, accent, iconBg, loading }) => (
  <Card sx={{
    flex: "1 1 130px", borderRadius: "16px", border: `1px solid ${T.border}`,
    boxShadow: "0 1px 4px rgba(15,23,42,0.06)", overflow: "hidden",
  }}>
    <Box sx={{ height: 3, background: `linear-gradient(90deg, ${accent}, ${accent}66)` }} />
    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 }, textAlign: "center" }}>
      {loading
        ? <Skeleton width={50} height={40} sx={{ mx: "auto" }} />
        : <Typography sx={{ fontSize: 28, fontWeight: 800, color: accent, lineHeight: 1.1, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {value ?? "—"}
          </Typography>
      }
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.muted, textTransform: "uppercase", letterSpacing: "0.4px", mt: 0.8 }}>
        {label}
      </Typography>
    </CardContent>
  </Card>
);

// ── Section card ──────────────────────────────────────────────────────────
const SCard = ({ title, subtitle, children }) => (
  <Card sx={{ borderRadius: "16px", border: `1px solid ${T.border}`, boxShadow: "0 1px 4px rgba(15,23,42,0.06)", height: "100%" }}>
    <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
      <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: "'Plus Jakarta Sans', sans-serif", mb: subtitle ? 0.3 : 2 }}>
        {title}
      </Typography>
      {subtitle && <Typography sx={{ fontSize: 12, color: T.muted, mb: 2 }}>{subtitle}</Typography>}
      {children}
    </CardContent>
  </Card>
);

// ── Custom tooltip ────────────────────────────────────────────────────────
const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{ bgcolor: "#fff", border: `1px solid ${T.border}`, borderRadius: "10px", p: 1.5, boxShadow: "0 4px 16px rgba(0,0,0,0.1)" }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.muted, mb: 0.5 }}>{label}</Typography>
      {payload.map(p => (
        <Box key={p.dataKey} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: p.fill || p.color }} />
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.text }}>{p.value} complaints</Typography>
        </Box>
      ))}
    </Box>
  );
};

// ── Main component ────────────────────────────────────────────────────────
export default function ComplaintStats() {
  const navigate = useNavigate();
  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  useEffect(() => {
    getData(API_ENDPOINTS.getComplaintStats)
      .then(res => { if (res?.result) setStats(res.result); })
      .catch(() => setError("Failed to load complaint statistics."))
      .finally(() => setLoading(false));
  }, []);

  if (error) return <Alert severity="error" sx={{ m: 3, borderRadius: "12px" }}>{error}</Alert>;

  const totalSla = (stats?.sla_breaches?.triage_breached || 0) + (stats?.sla_breaches?.resolution_breached || 0);

  // Chart data
  const statusData = stats?.by_status
    ? Object.entries(stats.by_status).map(([k, v]) => ({
        name: catLabel(k), count: v,
        fill: STATUS_COLORS[k] || T.muted,
      }))
    : [];

  const categoryData = stats?.by_category
    ? Object.entries(stats.by_category).map(([k, v]) => ({ name: catLabel(k), value: v }))
    : [];

  return (
    <Box sx={{ bgcolor: T.bg, minHeight: "100vh", pb: 4 }}>

      {/* ── Hero header ── */}
      <Box sx={{
        background: `linear-gradient(135deg, ${T.teal} 0%, ${T.tealDk} 100%)`,
        borderRadius: "20px", p: 3, mb: 3,
        boxShadow: "0 4px 20px rgba(0,124,132,0.2)",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <IconButton onClick={() => navigate(-1)}
            sx={{ bgcolor: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: "10px", color: "#fff", "&:hover": { bgcolor: "rgba(255,255,255,0.25)" } }}>
            <ArrowBackRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.7)", fontWeight: 600 }}>Complaints</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ width: 46, height: 46, borderRadius: "12px", bgcolor: "rgba(255,255,255,0.18)", border: "1px solid rgba(255,255,255,0.25)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <HeadsetMicRoundedIcon sx={{ fontSize: 24, color: "#fff" }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#fff", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Complaints Analytics</Typography>
            <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.75)", mt: 0.3 }}>
              Volume by status, category, and SLA compliance
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── KPI row ── */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
        <KpiTile label="Total"       value={stats?.total}     accent={T.teal}    loading={loading} />
        <KpiTile label="Open"        value={stats?.open}      accent={T.amber}   loading={loading} />
        <KpiTile label="Escalated"   value={stats?.escalated} accent={T.rose}    loading={loading} />
        <KpiTile label="Resolved"    value={stats?.resolved}  accent={T.emerald} loading={loading} />
        <KpiTile label="Closed"      value={stats?.closed}    accent={T.muted}   loading={loading} />
        <KpiTile label="SLA Breaches"value={totalSla}         accent={T.rose}    loading={loading} />
      </Box>

      {/* ── SLA Breakdown ── */}
      <Card sx={{ borderRadius: "16px", border: `1px solid ${T.border}`, boxShadow: "0 1px 4px rgba(15,23,42,0.06)", mb: 3 }}>
        <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.text, mb: 2, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            SLA Breach Breakdown
          </Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {[
              { label: "Triage SLA Breached",     value: stats?.sla_breaches?.triage_breached     || 0, accent: T.amber },
              { label: "Resolution SLA Breached",  value: stats?.sla_breaches?.resolution_breached || 0, accent: T.rose  },
              { label: "Total SLA Breached",       value: stats?.sla_breaches?.total_breached      || 0, accent: T.rose  },
            ].map(s => (
              <Box key={s.label} sx={{
                flex: "1 1 160px", p: 2.5, borderRadius: "14px", textAlign: "center",
                bgcolor: s.accent === T.rose ? T.roseLt : T.amberLt,
                border: `1px solid ${s.accent}22`,
              }}>
                {loading
                  ? <Skeleton width={50} height={36} sx={{ mx: "auto" }} />
                  : <Typography sx={{ fontSize: 28, fontWeight: 800, color: s.accent, lineHeight: 1.1, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{s.value}</Typography>
                }
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: s.accent, mt: 0.8, opacity: 0.85 }}>{s.label}</Typography>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* ── Charts row ── */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>

        {/* Status bar chart */}
        <Box sx={{ flex: "1.5 1 340px" }}>
          <SCard title="Complaints by Status" subtitle="Count per current status">
            {loading ? (
              <Box sx={{ height: 260, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Box sx={{ width: 28, height: 28, borderRadius: "50%", border: `3px solid ${T.teal}`, borderTopColor: "transparent", animation: "spin 0.8s linear infinite" }} />
              </Box>
            ) : statusData.length === 0 ? (
              <Typography fontSize={13} color="text.secondary">No data available.</Typography>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={statusData} barSize={32} margin={{ top: 4, right: 8, bottom: 40, left: 0 }}>
                  <defs>
                    {statusData.map((d, i) => (
                      <linearGradient key={i} id={`bar${i}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%"   stopColor={d.fill} stopOpacity={1} />
                        <stop offset="100%" stopColor={d.fill} stopOpacity={0.65} />
                      </linearGradient>
                    ))}
                  </defs>
                  <CartesianGrid strokeDasharray="4 4" stroke={T.border} vertical={false} />
                  <XAxis dataKey="name" angle={-30} textAnchor="end" tick={{ fontSize: 11, fill: T.muted }} axisLine={false} tickLine={false} interval={0} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: T.muted }} axisLine={false} tickLine={false} width={24} />
                  <Tooltip content={<ChartTip />} cursor={{ fill: T.tealLt }} />
                  <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                    {statusData.map((_, i) => <Cell key={i} fill={`url(#bar${i})`} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </SCard>
        </Box>

        {/* Category pie chart */}
        <Box sx={{ flex: "1 1 280px" }}>
          <SCard title="Complaints by Category" subtitle="Distribution across complaint types">
            {loading ? (
              <Box sx={{ height: 260, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Box sx={{ width: 28, height: 28, borderRadius: "50%", border: `3px solid ${T.teal}`, borderTopColor: "transparent", animation: "spin 0.8s linear infinite" }} />
              </Box>
            ) : categoryData.length === 0 ? (
              <Typography fontSize={13} color="text.secondary">No data available.</Typography>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={categoryData} cx="50%" cy="45%" outerRadius={90} innerRadius={40}
                    dataKey="value" paddingAngle={3}
                    label={({ name, percent }) => percent > 0.05 ? `${(percent * 100).toFixed(0)}%` : ""}
                    labelLine={false}
                  >
                    {categoryData.map((_, i) => (
                      <Cell key={i} fill={PIE_PALETTE[i % PIE_PALETTE.length]} stroke="none" />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTip />} />
                  <Legend
                    iconType="circle" iconSize={8}
                    wrapperStyle={{ fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                    formatter={v => <span style={{ color: T.muted }}>{v}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </SCard>
        </Box>
      </Box>
    </Box>
  );
}
